import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faEdit, faTrash, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { ConfirmDialogComponent } from '@frontend/shared';
import { User } from '@clientwire/api';
import { Dialog, DialogModule } from '@angular/cdk/dialog';
import { UserDialogComponent, UserDialogData } from './user-dialog/user-dialog.component';
import { UsersService } from './users.service';
import { faUnlock } from '@fortawesome/pro-solid-svg-icons';
import { ButtonComponent } from '@akostm/orbit-ui';

@Component({
  selector: 'lib-settings-users',
  standalone: true,
  imports: [CommonModule, TranslateModule, FontAwesomeModule, DialogModule, ButtonComponent],
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
})
export class UsersComponent implements OnInit {
  readonly #usersService = inject(UsersService);
  readonly #translateService = inject(TranslateService);
  readonly #dialog = inject(Dialog);

  // Users data
  readonly users = this.#usersService.users;
  readonly loading = this.#usersService.usersLoading;
  readonly error = this.#usersService.error;

  // Font Awesome icons
  readonly editIcon = faEdit;
  readonly deleteIcon = faTrash;
  readonly addIcon = faUserPlus;
  readonly unlockIcon = faUnlock;

  ngOnInit(): void {
    this.#usersService.loadUsers();
  }

  addUser(): void {
    const dialogRef = this.#dialog.open<any>(UserDialogComponent, {
      data: {} as UserDialogData,
      width: '32rem',
    });

    const dialogComponent = dialogRef.componentInstance as UserDialogComponent;
    dialogComponent.submitForm.subscribe((formData) => {
      this.#usersService.createUser(formData, dialogRef);
    });
  }

  editUser(user: User): void {
    const dialogRef = this.#dialog.open<any>(UserDialogComponent, {
      data: {
        user: user,
      } as UserDialogData,
      width: '32rem',
    });

    const dialogComponent = dialogRef.componentInstance as UserDialogComponent;
    dialogComponent.submitForm.subscribe((formData) => {
      this.#usersService.updateUser(user.id, formData, dialogRef);
    });
  }

  deleteUser(user: User): void {
    const dialogRef = this.#dialog.open<any>(UserDialogComponent, {
      data: {
        title: this.#translateService.instant('SETTINGS.USERS.CONFIRM_DELETE_TITLE'),
        confirmLabel: this.#translateService.instant('SETTINGS.USERS.DELETE'),
        message: this.#translateService.instant('SETTINGS.USERS.CONFIRM_DELETE', {
          email: user.email,
        }),
        isConfirmation: true,
      },
      width: '32rem',
    });

    dialogRef.closed.subscribe((result) => {
      if (result) {
        this.#usersService.deleteUser(user.id);
      }
    });
  }

  confirmUnlock(user: User) {
    const id = user.id;
    const dialogRef = this.#dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.#translateService.instant('SETTINGS.USERS.UNLOCK_ALL_CONVERSATIONS.TITLE'),
        message: this.#translateService.instant('SETTINGS.USERS.UNLOCK_ALL_CONVERSATIONS.MESSAGE'),
        confirmLabel: this.#translateService.instant(
          'SETTINGS.USERS.UNLOCK_ALL_CONVERSATIONS.CONFIRM_LABEL'
        ),
        cancelLabel: this.#translateService.instant(
          'SETTINGS.USERS.UNLOCK_ALL_CONVERSATIONS.CANCEL_LABEL'
        ),
      },
      width: '32rem',
    });

    dialogRef.closed.subscribe((result) => {
      if (result) {
        this.#usersService.unlockConversations(id);
      }
    });
  }
}
